import { Suspense, type Dispatch, type SetStateAction } from "react";
import {
    Autocomplete,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { FoodsCheckbox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import type { TransportLeg } from "src/api/client-api/transport/useTransportLegs";
import type { Geography } from "src/api/sure-api/useSearchGeographies";
import type { TransportModeChoice } from "src/api/sure-api/useSearchTransportModes";
import type { TransportReferenceLeg } from "src/api/sure-api/useSearchTransportReferenceLegs";
import { LazyTransportEmptyMapPreview, LazyTransportLegMap } from "src/components/Transport/Map";
import { FoodsLoadingIndicator, FoodsSelectField, FoodsTextField } from "src/components/EntityForm";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { TransportDistanceTag } from "src/components/Transport/TransportDistanceTag";

import {
    TransportCreatePageShell,
    TransportCreateSection,
    TransportFormMapLayout,
    TransportFormMapPanel,
    TransportFormTabPanel,
    TransportFormTabs,
    transportCreateTextFieldSx,
} from "./TransportCreatePageShell";
import { TransportScoresPanel } from "./TransportScoresPanel";
import { TransportLegGeocodeInput, TransportLegGeographyAutocomplete } from "./TransportLegLocationFields";
import { blankDraft, isRefrigeratedTransportCode, type LocalDraft } from "./transportLegCreateDraft";
import { useTransportAppearance } from "./transportAppearance";

const transportMapPreviewAspectRatio = "334 / 235";

type TransportLegCreateFormProps = {
    destinationGeography: Geography | null | undefined;
    draft: LocalDraft;
    editingLeg: TransportLeg | null;
    formDistanceKm: number | null;
    isEditingLegError: boolean;
    isEditingLinked: boolean;
    isEditMode: boolean;
    isReferenceLocked: boolean;
    linkedRefId: number | null;
    organizationId: number;
    originGeography: Geography | null | undefined;
    previewLeg: TransportLeg | null;
    routeComputeError?: unknown;
    routeComputeLabel: string;
    routeComputeLoading: boolean;
    saveDisabled: boolean;
    saveLoading: boolean;
    selectedTab: "form" | "scores";
    transportModes: TransportModeChoice[];
    transportReferenceLegs: TransportReferenceLeg[];
    onClearRoutePreview: () => void;
    onDistanceChange: (value: string) => void;
    onMapRouteCompute: (leg: TransportLeg) => void;
    onSave: () => Promise<void>;
    setDraft: Dispatch<SetStateAction<LocalDraft>>;
    setLinkedRefId: Dispatch<SetStateAction<number | null>>;
    setSelectedTab: Dispatch<SetStateAction<"form" | "scores">>;
};

export const TransportLegCreateForm = ({
    destinationGeography,
    draft,
    editingLeg,
    formDistanceKm,
    isEditingLegError,
    isEditingLinked,
    isEditMode,
    isReferenceLocked,
    linkedRefId,
    organizationId,
    originGeography,
    previewLeg,
    routeComputeError,
    routeComputeLabel,
    routeComputeLoading,
    saveDisabled,
    saveLoading,
    selectedTab,
    transportModes,
    transportReferenceLegs,
    onClearRoutePreview,
    onDistanceChange,
    onMapRouteCompute,
    onSave,
    setDraft,
    setLinkedRefId,
    setSelectedTab,
}: TransportLegCreateFormProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const appearance = useTransportAppearance("product-v2");

    return (
        <TransportCreatePageShell
            companyId={organizationId}
            title={isEditMode ? t("transport.legs.edit", "Edit leg") : t("transport.legs.create", "New leg")}
            saveDisabled={saveDisabled}
            saveLoading={saveLoading}
            onSave={onSave}
        >
            <TransportFormTabs
                ariaLabel={isEditMode ? t("transport.legs.edit", "Edit leg") : t("transport.legs.create", "New leg")}
                idPrefix="transport-leg"
                informationLabel={t("transport.tabs.information", "Information")}
                onChange={setSelectedTab}
                scoresLabel={t("transport.tabs.scores", "Scores")}
                value={selectedTab}
            />
            {isEditMode && isEditingLegError && editingLeg !== null && (
                <RemoteErrorAlert
                    message={t("transport.legs.refresh_error", "Unable to refresh transport leg. Showing saved data.")}
                    status="server-error"
                />
            )}
            {selectedTab === "scores" && (
                <TransportFormTabPanel idPrefix="transport-leg" value="scores">
                    <TransportScoresPanel kind="leg" transportId={editingLeg?.id} />
                </TransportFormTabPanel>
            )}
            {selectedTab === "form" && (
                <TransportFormTabPanel idPrefix="transport-leg" value="form">
                    <TransportFormMapLayout>
                        <Stack gap={2}>
                            {!isEditMode && (
                                <TransportCreateSection
                                    title={t("transport.create.sections.reference_transport", "Reference transport")}
                                    progress={{ current: 1, total: 2 }}
                                    description={t(
                                        "transport.create.reference_transport_help",
                                        "If you want to use generic data, you can choose a reference on the following list. If you prefer to use your own specific data, don’t select any reference.",
                                    )}
                                >
                                    {appearance === "legacy" ?
                                        <Autocomplete
                                            options={transportReferenceLegs}
                                            getOptionLabel={(option) => option.name ?? option.code}
                                            value={
                                                transportReferenceLegs.find(
                                                    (reference) => reference.id === linkedRefId,
                                                ) ?? null
                                            }
                                            onChange={(_event, value) => {
                                                onClearRoutePreview();
                                                setLinkedRefId(value?.id ?? null);
                                                if (value === null) {
                                                    setDraft(blankDraft);
                                                    return;
                                                }

                                                setDraft((current) => ({
                                                    ...current,
                                                    name: value.name ?? current.name,
                                                    transportModeId: value.transportModeReference.id,
                                                    distanceKm:
                                                        value.distanceKm !== null && value.distanceKm !== undefined ?
                                                            String(value.distanceKm)
                                                        :   current.distanceKm,
                                                    originCountryCode:
                                                        value.originCountryCode ?? current.originCountryCode,
                                                    destinationCountryCode:
                                                        value.destinationCountryCode ?? current.destinationCountryCode,
                                                    isRefrigerated:
                                                        value.isRefrigerated ||
                                                        value.transportModeReference.isRefrigerated === true ||
                                                        isRefrigeratedTransportCode(value.transportModeReference.code),
                                                    originLatitude: null,
                                                    originLongitude: null,
                                                    originLabel: "",
                                                    destinationLatitude: null,
                                                    destinationLongitude: null,
                                                    destinationLabel: "",
                                                }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t(
                                                        "transport.legs.from_reference",
                                                        "Link to a reference leg",
                                                    )}
                                                    placeholder={t(
                                                        "transport.create.select_reference",
                                                        "Select a reference",
                                                    )}
                                                    sx={transportCreateTextFieldSx(theme)}
                                                />
                                            )}
                                        />
                                    :   <FoodsSelectField
                                            appearance="product-v2"
                                            options={transportReferenceLegs}
                                            getId={(option) => option.id}
                                            getName={(option) => option.name ?? option.code}
                                            selectedOption={
                                                transportReferenceLegs.find(
                                                    (reference) => reference.id === linkedRefId,
                                                ) ?? null
                                            }
                                            onChange={(value) => {
                                                onClearRoutePreview();
                                                setLinkedRefId(value?.id ?? null);
                                                if (value === null) {
                                                    setDraft(blankDraft);
                                                    return;
                                                }

                                                setDraft((current) => ({
                                                    ...current,
                                                    name: value.name ?? current.name,
                                                    transportModeId: value.transportModeReference.id,
                                                    distanceKm:
                                                        value.distanceKm !== null && value.distanceKm !== undefined ?
                                                            String(value.distanceKm)
                                                        :   current.distanceKm,
                                                    originCountryCode:
                                                        value.originCountryCode ?? current.originCountryCode,
                                                    destinationCountryCode:
                                                        value.destinationCountryCode ?? current.destinationCountryCode,
                                                    isRefrigerated:
                                                        value.isRefrigerated ||
                                                        value.transportModeReference.isRefrigerated === true ||
                                                        isRefrigeratedTransportCode(value.transportModeReference.code),
                                                    originLatitude: null,
                                                    originLongitude: null,
                                                    originLabel: "",
                                                    destinationLatitude: null,
                                                    destinationLongitude: null,
                                                    destinationLabel: "",
                                                }));
                                            }}
                                            label={t("transport.legs.from_reference", "Link to a reference leg")}
                                            placeholder={t("transport.create.select_reference", "Select a reference")}
                                            searchPlaceholder={t("component.select.searchplaceholder", "Search…")}
                                            emptyOptionsText={t("component.select.nooptions", "No option available")}
                                            noResultText={t("component.select.noresultfound", "No results found")}
                                            noValue={t("component.select.unselectvalue", "Don't select anything")}
                                        />
                                    }
                                </TransportCreateSection>
                            )}

                            <TransportCreateSection
                                title={t("transport.create.sections.leg_properties", "Transport properties")}
                                progress={isEditMode ? undefined : { current: 2, total: 2 }}
                                description={t(
                                    "transport.create.leg_properties_help",
                                    "Information needed to calculate your transportation scores.",
                                )}
                            >
                                {appearance === "legacy" ?
                                    <>
                                        <TextField
                                            label={t("transport.create.leg_name", "Leg name*")}
                                            value={draft.name}
                                            onChange={(event) =>
                                                setDraft((current) => ({ ...current, name: event.target.value }))
                                            }
                                            disabled={isReferenceLocked}
                                            fullWidth
                                            sx={transportCreateTextFieldSx(theme)}
                                        />

                                        <Autocomplete
                                            options={transportModes}
                                            getOptionLabel={(option) => option.label}
                                            value={
                                                transportModes.find((mode) => mode.id === draft.transportModeId) ?? null
                                            }
                                            onChange={(_event, value) => {
                                                onClearRoutePreview();
                                                setDraft((current) => ({
                                                    ...current,
                                                    transportModeId: value?.id ?? null,
                                                    isRefrigerated: value?.isRefrigerated ?? false,
                                                }));
                                            }}
                                            disabled={isReferenceLocked}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t("transport.leg.mode", "Transport mode")}
                                                    placeholder={t(
                                                        "transport.create.select_transport_mode",
                                                        "Select a transport mode",
                                                    )}
                                                    sx={transportCreateTextFieldSx(theme)}
                                                />
                                            )}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={draft.isRefrigerated}
                                                    onChange={(event) =>
                                                        setDraft((current) => ({
                                                            ...current,
                                                            isRefrigerated: event.target.checked,
                                                        }))
                                                    }
                                                    disabled={isReferenceLocked}
                                                />
                                            }
                                            label={t("transport.mode.refrigerated", "Refrigerated")}
                                        />

                                        <TransportLegGeocodeInput
                                            label={t("transport.leg.origin_address", "Origin address")}
                                            currentLabel={draft.originLabel}
                                            latitude={draft.originLatitude}
                                            longitude={draft.originLongitude}
                                            disabled={isReferenceLocked}
                                            onPick={(picked) => {
                                                onClearRoutePreview();
                                                setDraft((current) => ({
                                                    ...current,
                                                    originLatitude: picked?.latitude ?? null,
                                                    originLongitude: picked?.longitude ?? null,
                                                    originLabel: picked?.label ?? "",
                                                    originCountryCode:
                                                        picked?.countryCode?.toUpperCase() ?? current.originCountryCode,
                                                }));
                                            }}
                                        />

                                        <TransportLegGeocodeInput
                                            label={t("transport.leg.destination_address", "Destination address")}
                                            currentLabel={draft.destinationLabel}
                                            latitude={draft.destinationLatitude}
                                            longitude={draft.destinationLongitude}
                                            disabled={isReferenceLocked}
                                            onPick={(picked) => {
                                                onClearRoutePreview();
                                                setDraft((current) => ({
                                                    ...current,
                                                    destinationLatitude: picked?.latitude ?? null,
                                                    destinationLongitude: picked?.longitude ?? null,
                                                    destinationLabel: picked?.label ?? "",
                                                    destinationCountryCode:
                                                        picked?.countryCode?.toUpperCase() ??
                                                        current.destinationCountryCode,
                                                }));
                                            }}
                                        />

                                        <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                                            <TransportLegGeographyAutocomplete
                                                label={t("transport.leg.origin", "Origin")}
                                                value={draft.originCountryCode}
                                                selectedGeography={originGeography}
                                                disabled={isReferenceLocked}
                                                onChange={(code) => {
                                                    onClearRoutePreview();
                                                    setDraft((current) => ({
                                                        ...current,
                                                        originCountryCode: code ?? "",
                                                    }));
                                                }}
                                            />
                                            <TransportLegGeographyAutocomplete
                                                label={t("transport.leg.destination", "Destination")}
                                                value={draft.destinationCountryCode}
                                                selectedGeography={destinationGeography}
                                                disabled={isReferenceLocked}
                                                onChange={(code) => {
                                                    onClearRoutePreview();
                                                    setDraft((current) => ({
                                                        ...current,
                                                        destinationCountryCode: code ?? "",
                                                    }));
                                                }}
                                            />
                                        </Stack>

                                        <TextField
                                            label={t("transport.leg.distance", "Distance (km)")}
                                            type="number"
                                            value={draft.distanceKm}
                                            onChange={(event) => onDistanceChange(event.target.value)}
                                            disabled={isReferenceLocked}
                                            sx={transportCreateTextFieldSx(theme)}
                                            slotProps={{ htmlInput: { min: 0, step: "any" } }}
                                        />
                                    </>
                                :   <>
                                        <Box
                                            data-testid="transport-leg-primary-fields"
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "1fr",
                                                    md: "minmax(0, 1fr) minmax(0, 1fr)",
                                                },
                                                gap: theme.spacing(3),
                                            }}
                                        >
                                            <FoodsTextField
                                                label={t("transport.create.leg_name", "Leg name*")}
                                                value={draft.name}
                                                onChange={(name) => setDraft((current) => ({ ...current, name }))}
                                                disabled={isReferenceLocked}
                                            />

                                            <FoodsSelectField
                                                appearance="product-v2"
                                                options={transportModes}
                                                getId={(option) => option.id}
                                                getName={(option) => option.label}
                                                selectedOption={
                                                    transportModes.find((mode) => mode.id === draft.transportModeId) ??
                                                    null
                                                }
                                                onChange={(value) => {
                                                    onClearRoutePreview();
                                                    setDraft((current) => ({
                                                        ...current,
                                                        transportModeId: value?.id ?? null,
                                                        isRefrigerated: value?.isRefrigerated ?? false,
                                                    }));
                                                }}
                                                disabled={isReferenceLocked}
                                                label={t("transport.leg.mode", "Transport mode")}
                                                placeholder={t(
                                                    "transport.create.select_transport_mode",
                                                    "Select a transport mode",
                                                )}
                                                searchPlaceholder={t("component.select.searchplaceholder", "Search…")}
                                                emptyOptionsText={t(
                                                    "component.select.nooptions",
                                                    "No option available",
                                                )}
                                                noResultText={t("component.select.noresultfound", "No results found")}
                                                noValue={t("component.select.unselectvalue", "Don't select anything")}
                                            />
                                        </Box>

                                        <FoodsCheckbox
                                            values={[true]}
                                            selectedItems={draft.isRefrigerated ? [true] : []}
                                            onChange={(selectedItems) =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    isRefrigerated: selectedItems.length > 0,
                                                }))
                                            }
                                            getId={() => "refrigerated"}
                                            getName={() => t("transport.mode.refrigerated", "Refrigerated")}
                                            CheckboxProps={{ disabled: isReferenceLocked }}
                                            FormControlLabelProps={{ sx: { margin: 0 } }}
                                        />

                                        <TransportLegGeocodeInput
                                            label={t("transport.leg.origin_address", "Origin address")}
                                            currentLabel={draft.originLabel}
                                            disabled={isReferenceLocked}
                                            onPick={(picked) => {
                                                onClearRoutePreview();
                                                setDraft((current) => ({
                                                    ...current,
                                                    originLatitude: picked?.latitude ?? null,
                                                    originLongitude: picked?.longitude ?? null,
                                                    originLabel: picked?.label ?? "",
                                                    originCountryCode:
                                                        picked?.countryCode?.toUpperCase() ?? current.originCountryCode,
                                                }));
                                            }}
                                        />

                                        <TransportLegGeocodeInput
                                            label={t("transport.leg.destination_address", "Destination address")}
                                            currentLabel={draft.destinationLabel}
                                            disabled={isReferenceLocked}
                                            onPick={(picked) => {
                                                onClearRoutePreview();
                                                setDraft((current) => ({
                                                    ...current,
                                                    destinationLatitude: picked?.latitude ?? null,
                                                    destinationLongitude: picked?.longitude ?? null,
                                                    destinationLabel: picked?.label ?? "",
                                                    destinationCountryCode:
                                                        picked?.countryCode?.toUpperCase() ??
                                                        current.destinationCountryCode,
                                                }));
                                            }}
                                        />

                                        <Box
                                            data-testid="transport-leg-route-fields"
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "1fr",
                                                    md: "minmax(0, 1fr) minmax(0, 1fr) max-content",
                                                },
                                                gap: theme.spacing(3),
                                            }}
                                        >
                                            <TransportLegGeographyAutocomplete
                                                label={t("transport.leg.origin", "Origin")}
                                                value={draft.originCountryCode}
                                                selectedGeography={originGeography}
                                                disabled={isReferenceLocked}
                                                onChange={(code) => {
                                                    onClearRoutePreview();
                                                    setDraft((current) => ({
                                                        ...current,
                                                        originCountryCode: code ?? "",
                                                    }));
                                                }}
                                            />
                                            <TransportLegGeographyAutocomplete
                                                label={t("transport.leg.destination", "Destination")}
                                                value={draft.destinationCountryCode}
                                                selectedGeography={destinationGeography}
                                                disabled={isReferenceLocked}
                                                onChange={(code) => {
                                                    onClearRoutePreview();
                                                    setDraft((current) => ({
                                                        ...current,
                                                        destinationCountryCode: code ?? "",
                                                    }));
                                                }}
                                            />
                                            <TransportDistanceTag
                                                value={
                                                    formDistanceKm !== null && Number.isFinite(formDistanceKm) ?
                                                        formDistanceKm
                                                    :   null
                                                }
                                                label={t("transport.leg.distance_label", "Distance")}
                                                emptyLabel={t(
                                                    "transport.leg.distance_unavailable",
                                                    "Distance unavailable",
                                                )}
                                            />
                                        </Box>
                                    </>
                                }
                            </TransportCreateSection>
                        </Stack>
                        <TransportFormMapPanel title={t("transport.create.map_title", "Transport map")}>
                            {appearance === "legacy" ?
                                <Box
                                    sx={{
                                        borderRadius: theme.spacing(1.5),
                                        overflow: "hidden",
                                        backgroundColor: theme.custom.grey[300],
                                    }}
                                >
                                    {previewLeg ?
                                        <Suspense
                                            fallback={
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    minHeight={420}
                                                >
                                                    <CircularProgress />
                                                </Box>
                                            }
                                        >
                                            <LazyTransportLegMap
                                                leg={previewLeg}
                                                allowRouteCompute={!isEditingLinked && linkedRefId === null}
                                                onRouteCompute={onMapRouteCompute}
                                                routeComputeLabel={routeComputeLabel}
                                                routeComputeLoading={routeComputeLoading}
                                                routeComputeError={routeComputeError}
                                                heightPx={420}
                                            />
                                        </Suspense>
                                    :   <Stack alignItems="center" justifyContent="center" minHeight={420} p={3}>
                                            <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                                {t(
                                                    "transport.create.map_empty",
                                                    "Select origin and destination information to preview the route.",
                                                )}
                                            </Typography>
                                        </Stack>
                                    }
                                </Box>
                            :   <Box sx={{ minWidth: 0 }}>
                                    {previewLeg ?
                                        <Suspense
                                            fallback={
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    sx={{ aspectRatio: transportMapPreviewAspectRatio }}
                                                >
                                                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                                </Box>
                                            }
                                        >
                                            <LazyTransportLegMap
                                                leg={previewLeg}
                                                allowRouteCompute={!isEditingLinked && linkedRefId === null}
                                                onRouteCompute={onMapRouteCompute}
                                                routeComputeLabel={routeComputeLabel}
                                                routeComputeLoading={routeComputeLoading}
                                                routeComputeError={routeComputeError}
                                                aspectRatio={transportMapPreviewAspectRatio}
                                            />
                                        </Suspense>
                                    :   <Suspense
                                            fallback={
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    sx={{ aspectRatio: transportMapPreviewAspectRatio }}
                                                >
                                                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                                </Box>
                                            }
                                        >
                                            <LazyTransportEmptyMapPreview
                                                accessibleLabel={t(
                                                    "transport.create.map_empty",
                                                    "Select origin and destination information to preview the route.",
                                                )}
                                                computeRouteLabel={t(
                                                    "transport.map.compute_route",
                                                    "Compute real route",
                                                )}
                                                aspectRatio={transportMapPreviewAspectRatio}
                                            />
                                        </Suspense>
                                    }
                                </Box>
                            }
                        </TransportFormMapPanel>
                    </TransportFormMapLayout>
                </TransportFormTabPanel>
            )}
        </TransportCreatePageShell>
    );
};
